import { isRequestGM } from "../components/requests/requestPermissions";
import { schedulingEntryBoutique } from "../lib/schedulingEntry";
import useFocusState from "../hooks/useFocusState";
import RequestVessel from "../components/requests/RequestVessel";
import TimeOffForm from "../components/requests/TimeOffForm";
import React, { useCallback, useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import Avatar from "../components/Avatar";
import {
  Plus, CheckCircle2, XCircle, AlertTriangle,
  Trash2,
} from "lucide-react";

/**
 * Time off — Phase 4a of Scheduling.
 *
 * Two audiences, one page:
 *   - Front-line employees (MTs, EGs, front desk) see & submit their own requests.
 *   - GMs see the boutique's pending queue + approve/decline.
 *
 * Design rule: the original `request` is never overwritten. When a manager
 * approves with an adjustment, the divergence is shown side-by-side.
 */

const STATUS = {
  requested:         { dot: "bg-amber-500",   label: "Pending review" },
  approved:          { dot: "bg-emerald-500", label: "Approved · pending schedule review" },
  declined:          { dot: "bg-rose-500",    label: "Declined" },
  schedule_updated:  { dot: "bg-indigo-500",  label: "Schedule updated" },
  zenoti_pending:    { dot: "bg-indigo-500",  label: "Zenoti update pending" },
  complete:          { dot: "bg-emerald-500", label: "Complete" },
};

const TIME_OFF_TYPES = [
  ["paid_time_off", "Paid Time Off"], ["unpaid_time_off", "Unpaid Time Off"],
  ["bereavement", "Bereavement"], ["military", "Military"], ["jury_duty", "Jury Duty"],
  ["maternity", "Maternity"], ["other", "Other"],
];

function fmtRange(start, end) {
  return start === end ? start : `${start} → ${end}`;
}
function fmtTime(t) {
  if (!t) return "";
  const [h, m] = t.split(":").map(Number);
  const hh = ((h + 11) % 12) + 1;
  const ampm = h >= 12 ? "PM" : "AM";
  return `${hh}:${String(m).padStart(2, "0")} ${ampm}`;
}

function StatusPill({ status }) {
  const s = STATUS[status] || STATUS.requested;
  return (
    <span className="inline-flex items-center gap-1.5 text-[11px] schedule-muted bg-stone-50 px-2.5 py-0.5 rounded-full" data-testid={`status-${status}`}>
      <span className={`inline-block w-1.5 h-1.5 rounded-full ${s.dot}`} />
      {s.label}
    </span>
  );
}

function ConflictWarnings({ conflicts }) {
  if (!conflicts?.length) return null;
  return (
    <div className="rounded-2xl bg-amber-50 border border-amber-100 p-4 space-y-2" data-testid="time-off-conflicts">
      <div className="flex items-center gap-2 text-[11px] label-eyebrow text-amber-800">
        <AlertTriangle size={12} strokeWidth={1.5} /> Advisory warnings — approval is still possible
      </div>
      <ul className="space-y-1 text-sm text-amber-900">
        {conflicts.map((c, i) => (
          <li key={i} data-testid={`time-off-conflict-${i}`}>· {c.message}</li>
        ))}
      </ul>
    </div>
  );
}

function ReviewPanel({ req, onApprove, onDecline }) {
  const [mode, setMode] = useState(null); // "approve" | "decline"
  const [adjust, setAdjust] = useState(false);
  const [outcome, setOutcome] = useState({
    start_date: req.request.start_date,
    end_date: req.request.end_date,
    full_day: req.request.full_day,
    start_time: req.request.start_time || "09:00",
    end_time: req.request.end_time || "17:00",
  });
  const [reviewNotes, setReviewNotes] = useState("");
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState("");

  if (!req.can_approve && !req.can_decline) return null;

  const doApprove = async () => {
    setSaving(true); setErr("");
    try {
      const payload = adjust ? {
        start_date: outcome.start_date, end_date: outcome.end_date,
        full_day: outcome.full_day,
        start_time: outcome.full_day ? null : outcome.start_time,
        end_time: outcome.full_day ? null : outcome.end_time,
        review_notes: reviewNotes,
      } : { review_notes: reviewNotes };
      await onApprove(payload);
      setMode(null);
    } catch (e) { setErr(e.response?.data?.detail || "Approve failed."); }
    finally { setSaving(false); }
  };
  const doDecline = async () => {
    setSaving(true); setErr("");
    try {
      await onDecline({ review_notes: reviewNotes });
      setMode(null);
    } catch (e) { setErr(e.response?.data?.detail || "Decline failed."); }
    finally { setSaving(false); }
  };

  return (
    <div className="schedule-panel schedule-border rounded-3xl p-6  space-y-4" data-testid="time-off-review">
      <div className="flex items-center gap-2 flex-wrap">
        {req.can_approve && (
          <button onClick={() => setMode(mode === "approve" ? null : "approve")} data-testid="time-off-approve-toggle"
            className="text-sm px-4 py-2 rounded-full bg-emerald-600 text-white hover:bg-emerald-700 flex items-center gap-2">
            <CheckCircle2 size={14} strokeWidth={1.5} /> Approve
          </button>
        )}
        {req.can_decline && (
          <button onClick={() => setMode(mode === "decline" ? null : "decline")} data-testid="time-off-decline-toggle"
            className="text-sm px-4 py-2 rounded-full bg-rose-600 text-white hover:bg-rose-700 flex items-center gap-2">
            <XCircle size={14} strokeWidth={1.5} /> Decline
          </button>
        )}
      </div>

      {mode === "approve" && (
        <div className="rounded-2xl bg-emerald-50 p-4 space-y-3" data-testid="time-off-approve-form">
          <label className="flex items-center gap-2 text-sm text-emerald-900">
            <input type="checkbox" checked={adjust} onChange={(e) => setAdjust(e.target.checked)} data-testid="time-off-adjust-toggle" />
            <span>Approve with an adjusted outcome (original request will be preserved)</span>
          </label>
          {req.request.backup_start_date && (
            <button type="button" data-testid="time-off-use-backup"
              onClick={() => {
                setAdjust(true);
                setOutcome({ ...outcome, start_date: req.request.backup_start_date, end_date: req.request.backup_end_date });
              }}
              className="text-left text-xs text-emerald-800 hover:text-emerald-950 underline underline-offset-2">
              Use employee backup dates: {fmtRange(req.request.backup_start_date, req.request.backup_end_date)}
            </button>
          )}
          {adjust && (
            <div className="grid grid-cols-1 sm:grid-cols-4 gap-2 items-end" data-testid="time-off-adjust-form">
              <div>
                <div className="text-[10px] label-eyebrow schedule-muted mb-1">Start</div>
                <input type="date" value={outcome.start_date} onChange={(e) => setOutcome({ ...outcome, start_date: e.target.value })}
                  className="w-full px-2 py-1 text-xs rounded-lg schedule-border outline-none schedule-panel" aria-label="Approved start date" data-testid="approve-outcome-start" />
              </div>
              <div>
                <div className="text-[10px] label-eyebrow schedule-muted mb-1">End</div>
                <input type="date" value={outcome.end_date} onChange={(e) => setOutcome({ ...outcome, end_date: e.target.value })}
                  className="w-full px-2 py-1 text-xs rounded-lg schedule-border outline-none schedule-panel" aria-label="Approved end date" data-testid="approve-outcome-end" />
              </div>
              <div>
                <div className="text-[10px] label-eyebrow schedule-muted mb-1">Type</div>
                <div className="flex items-center gap-1">
                  <button type="button" onClick={() => setOutcome({ ...outcome, full_day: true })} aria-pressed={outcome.full_day} data-testid="approve-outcome-full-day"
                    className={`text-[11px] px-2 py-1 rounded-full ${outcome.full_day ? "bg-stone-900 text-white" : "bg-stone-100 schedule-muted"}`}>Full</button>
                  <button type="button" onClick={() => setOutcome({ ...outcome, full_day: false })} aria-pressed={!outcome.full_day} data-testid="approve-outcome-partial-day"
                    className={`text-[11px] px-2 py-1 rounded-full ${!outcome.full_day ? "bg-stone-900 text-white" : "bg-stone-100 schedule-muted"}`}>Partial</button>
                </div>
              </div>
              {!outcome.full_day && (
                <div className="flex items-center gap-1">
                  <input type="time" value={outcome.start_time} onChange={(e) => setOutcome({ ...outcome, start_time: e.target.value })}
                    className="w-full px-2 py-1 text-xs rounded-lg schedule-border outline-none schedule-panel" aria-label="Approved start time" data-testid="approve-outcome-start-time" />
                  <span className="text-[10px] schedule-muted">–</span>
                  <input type="time" value={outcome.end_time} onChange={(e) => setOutcome({ ...outcome, end_time: e.target.value })}
                    className="w-full px-2 py-1 text-xs rounded-lg schedule-border outline-none schedule-panel" aria-label="Approved end time" data-testid="approve-outcome-end-time" />
                </div>
              )}
            </div>
          )}
          <textarea value={reviewNotes} onChange={(e) => setReviewNotes(e.target.value)} rows={2} placeholder="Note to the employee (optional)"
            className="w-full text-sm px-3 py-2 rounded-lg schedule-border outline-none schedule-panel" aria-label="Approval note to employee" data-testid="time-off-approve-note" />
          {err && <div className="text-sm text-rose-600">{err}</div>}
          <button onClick={doApprove} disabled={saving} data-testid="time-off-approve-confirm"
            className="text-sm px-4 py-2 rounded-full bg-emerald-600 text-white hover:bg-emerald-700 disabled:opacity-40">
            {saving ? "…" : "Confirm approval"}
          </button>
        </div>
      )}

      {mode === "decline" && (
        <div className="rounded-2xl bg-rose-50 p-4 space-y-3" data-testid="time-off-decline-form">
          <textarea value={reviewNotes} onChange={(e) => setReviewNotes(e.target.value)} rows={3} placeholder="Note to the employee (optional)"
            className="w-full text-sm px-3 py-2 rounded-lg schedule-border outline-none schedule-panel" aria-label="Decline note to employee" data-testid="time-off-decline-note" />
          {err && <div className="text-sm text-rose-600">{err}</div>}
          <button onClick={doDecline} disabled={saving} data-testid="time-off-decline-confirm"
            className="text-sm px-4 py-2 rounded-full bg-rose-600 text-white hover:bg-rose-700 disabled:opacity-40">
            {saving ? "…" : "Confirm decline"}
          </button>
        </div>
      )}
    </div>
  );
}

function RequestVsOutcome({ req }) {
  const r = req.request;
  const o = req.approved_outcome;
  const rows = [
    { key: "Range",   left: fmtRange(r.start_date, r.end_date), right: o ? fmtRange(o.start_date, o.end_date) : null },
    { key: "Type",    left: r.full_day ? "Full day" : `${fmtTime(r.start_time)}–${fmtTime(r.end_time)}`,
                      right: o ? (o.full_day ? "Full day" : `${fmtTime(o.start_time)}–${fmtTime(o.end_time)}`) : null },
  ];
  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3" data-testid="time-off-request-outcome">
      <div className="schedule-panel schedule-border rounded-2xl p-4">
        <div className="text-[10px] label-eyebrow schedule-muted mb-2">Employee requested</div>
        {rows.map((row) => (
          <div key={row.key} className="text-sm py-1 flex items-center gap-3">
            <span className="w-16 text-[11px] schedule-muted">{row.key}</span>
            <span className="schedule-ink">{row.left}</span>
          </div>
        ))}
        {r.reason && <div className="text-xs schedule-muted mt-2">Type: {TIME_OFF_TYPES.find(([value]) => value === r.reason)?.[1] || r.reason}</div>}
        {r.notes && <div className="text-xs schedule-muted mt-1">Notes: {r.notes}</div>}
        {r.department && <div className="text-sm mt-1">Department: {r.department}</div>}
        {r.supervisor_name && <div className="text-sm mt-1">Supervisor: {r.supervisor_name}</div>}
        {r.return_date && <div className="text-sm mt-1">Return: {r.return_date}</div>}
        {r.backup_return_date && <div className="text-sm mt-1">Alternate return: {r.backup_return_date}</div>}
        {r.backup_start_date && <div className="text-xs schedule-muted mt-1">Backup: {fmtRange(r.backup_start_date, r.backup_end_date)}</div>}
      </div>
      <div className={`schedule-border rounded-2xl p-4 ${o ? "bg-emerald-50" : "bg-stone-50"}`}>
        <div className="text-[10px] label-eyebrow schedule-muted mb-2">Manager approved</div>
        {o ? rows.map((row) => (
          <div key={row.key} className="text-sm py-1 flex items-center gap-3">
            <span className="w-16 text-[11px] schedule-muted">{row.key}</span>
            <span className="schedule-ink">{row.right}</span>
            {row.left !== row.right && <span className="text-[10px] text-amber-700">adjusted</span>}
          </div>
        )) : (
          <div className="text-sm schedule-muted">Pending decision.</div>
        )}
        {req.review_notes && <div className="text-xs schedule-muted mt-2">Note: {req.review_notes}</div>}
      </div>
    </div>
  );
}

function RequestDetail({ req, canManage, refresh }) {

  const approve = async (payload) => { await api.post(`/scheduling/time-off/${req.id}/approve`, payload); await refresh(); };
  const decline = async (payload) => { await api.post(`/scheduling/time-off/${req.id}/decline`, payload); await refresh(); };
  const cancel = async () => { if (!window.confirm("Cancel this request?")) return; await api.post(`/scheduling/time-off/${req.id}/cancel`); await refresh(); };

  return (
    <div className="space-y-4">
      <div className="schedule-panel schedule-border rounded-3xl p-5 " data-testid={`time-off-detail-${req.id}`}>
        <div className="flex items-baseline justify-between flex-wrap gap-2">
          <div className="flex items-center gap-3 min-w-0">
            <Avatar name={req.requester_name} size={28} />
            <div className="min-w-0">
              <div className="schedule-ink font-medium">{req.requester_name}</div>
              <div className="text-[11px] schedule-muted">{req.boutique_code} · {req.requester_role?.replace(/_/g, " ")}</div>
            </div>
          </div>
          <StatusPill status={req.status} />
        </div>
        <div className="mt-4">
          <RequestVsOutcome req={req} />
          {req.request.employee_signature && <p className="mt-3 text-sm">Employee signature: {req.request.employee_signature.name} · {new Date(req.request.employee_signature.signed_at).toLocaleString()}</p>}
          {req.reviewer_name && <p className="mt-2 text-sm">Management decision: {req.status} · {req.reviewer_name} · {req.reviewed_at ? new Date(req.reviewed_at).toLocaleString() : ""}</p>}
        </div>
      </div>

      <ConflictWarnings conflicts={req.conflicts} />

      {canManage && <ReviewPanel req={req} onApprove={approve} onDecline={decline} />}

      {canManage && req.status === "approved" && <div className="rounded-2xl bg-emerald-50 p-4 text-sm text-emerald-900">Approved time off will be applied when the EG schedule is built. Zenoti is updated only after Katrina publishes the schedule.</div>}

      {req.can_cancel && (
        <button onClick={cancel} data-testid="time-off-cancel" className="text-sm schedule-muted hover:text-rose-600 flex items-center gap-1.5">
          <Trash2 size={12} strokeWidth={1.5} /> Cancel request
        </button>
      )}
    </div>
  );
}

function RequestListItem({ r, onOpen, active }) {
  const outcome = r.approved_outcome || r.request;
  return (
    <button
      onClick={() => onOpen(r.id)}
      data-testid={`time-off-item-${r.id}`}
      aria-pressed={active}
      className={`schedule-list-row w-full text-left p-4 flex items-center gap-3 transition-colors ${active ? "bg-stone-50" : "hover:bg-stone-50"}`}
    >
      <Avatar name={r.requester_name} size={28} />
      <div className="min-w-0 flex-1">
        <div className="text-sm schedule-ink truncate">{r.requester_name}</div>
        <div className="text-[11px] schedule-muted truncate">
          {fmtRange(outcome.start_date, outcome.end_date)} · {outcome.full_day ? "Full day" : "Partial"}
        </div>
      </div>
      <StatusPill status={r.status} />
    </button>
  );
}

export default function TimeOff() {
  const { user } = useAuth();
  const [entryParams] = useSearchParams();
  const entryBoutique = schedulingEntryBoutique(user, entryParams.get("boutique"));
  const initialBoutique = entryBoutique;
  const [boutique, setBoutique] = useState(initialBoutique);
  const [tab, setTab] = useFocusState(entryParams.get("request") || entryParams.get("view") === "all" ? "all" : "pending"); // "pending" | "mine" | "all"
  const [requests, setRequests] = useState([]);
  const [selectedId, setSelectedId] = useState(null);
  const [selected, setSelected] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [loading, setLoading] = useState(true);
  const [listError, setListError] = useState("");
  const [detailError, setDetailError] = useState("");

  const isLeadership = user && !user.boutique_code;
  const isFrontLine = user && ["front_desk", "experience_guide", "lead_experience_guide", "massage_therapist"].includes(user.role);
  const boutiques = ["PLT", "FLL", "WYN", "PNC"];

  const loadList = useCallback(async () => {
    setLoading(true); setListError("");
    try {
      let params = {};
      if (tab === "mine" || isFrontLine) params.mine = true;
      else if (tab === "pending") { params.boutique = boutique; params.status = "requested"; }
      else params.boutique = boutique;
      const r = await api.get("/scheduling/time-off", { params });
      const list = r.data?.requests || [];
      setRequests(list);
      // Auto-select the first request so the approve/deny actions are immediately visible.
      // Only auto-select if nothing is currently selected AND the current selection
      // isn't in the fresh list (avoids stealing focus during refresh).
      if (!selectedId && list.some(x=>x.id===entryParams.get("request"))) {
        setSelectedId(entryParams.get("request"));
      } else if (list.length > 0 && (!selectedId || !list.find((x) => x.id === selectedId))) {
        setSelectedId(list[0].id);
      } else if (list.length === 0) {
        setSelectedId(null);
      }
    } catch { setListError("Requests could not load. Retry to retrieve the saved records."); } finally { setLoading(false); }
  }, [boutique, isFrontLine, selectedId, tab, entryParams]);

  useEffect(() => { loadList(); }, [loadList]);

  useEffect(() => {
    if (!selectedId) { setSelected(null); setDetailError(""); return; }
    setDetailError("");
    api.get(`/scheduling/time-off/${selectedId}`).then((r) => setSelected(r.data)).catch(() => { setSelected(null); setDetailError("Request details could not load. Retry to retrieve the saved record."); });
  }, [selectedId]);

  const refresh = async () => {
    await loadList();
    if (selectedId) {
      try { const r = await api.get(`/scheduling/time-off/${selectedId}`); setSelected(r.data); setDetailError(""); }
      catch { setDetailError("Request details could not refresh. Retry to retrieve the saved record."); }
    }
  };

  return (
    <RequestVessel title="Time Off" description="A little breathing room, thoughtfully planned." toolbar={<div className="flex items-baseline justify-between flex-wrap gap-3">

          <div className="flex items-center gap-2">
            {isLeadership && (
              <select aria-label="Boutique" value={boutique} onChange={(e) => setBoutique(e.target.value)} data-testid="time-off-boutique"
                className="px-4 py-2 rounded-full schedule-border text-sm schedule-panel outline-none">
                {boutiques.map((b) => <option key={b} value={b}>{b}</option>)}
              </select>
            )}
            <button onClick={() => setShowForm((v) => !v)} aria-expanded={showForm} data-testid="time-off-new-toggle"
              className="text-sm px-4 py-2 rounded-full bg-stone-900 text-white hover:bg-stone-800 flex items-center gap-2">
              <Plus size={14} strokeWidth={1.5} /> New request
            </button>
            {showForm && <button type="button" onClick={() => setShowForm(false)}>Request history</button>}
          </div>
        </div>}>


        {showForm && (
          <div className="mt-6 request-form-wrap">
            <TimeOffForm user={user} boutique={boutique} onCreated={() => { setShowForm(false); refresh(); }} />
          </div>
        )}

        {listError && <div role="alert" className="request-message">{listError}<button type="button" onClick={loadList}>Retry request list</button></div>}
        {detailError && <div role="alert" className="request-message">{detailError}<button type="button" onClick={refresh}>Retry request details</button></div>}
        {!showForm && <div className="mt-8 grid grid-cols-1 md:grid-cols-3 gap-6">
          <div className="md:col-span-1 space-y-3">
            {!isFrontLine && (
              <div className="sonmi-nav sonmi-nav--primary flex items-center gap-1" data-testid="time-off-tabs">
                {[{ id: "pending", label: "Pending" }, { id: "all", label: "All" }, { id: "mine", label: "Mine" }].map((t) => (
                  <button key={t.id} aria-pressed={tab === t.id} onClick={() => setTab(t.id)} data-testid={`time-off-tab-${t.id}`}
                    className={`text-xs px-3 py-1.5 rounded-full ${tab === t.id ? "bg-stone-900 text-white" : "schedule-panel schedule-border schedule-muted hover:bg-stone-50"}`}>
                    {t.label}
                  </button>
                ))}
              </div>
            )}
            <div className="schedule-panel schedule-border rounded-2xl divide-y divide-stone-100 overflow-hidden" data-testid="time-off-list">
              {loading ? (
                <div className="p-6 text-sm schedule-muted">Loading…</div>
              ) : requests.length === 0 ? (
                <div className="p-6 text-sm schedule-muted">No requests here yet.</div>
              ) : requests.map((r) => (
                <RequestListItem key={r.id} r={r} onOpen={setSelectedId} active={selectedId === r.id} />
              ))}
            </div>
          </div>
          <div className="md:col-span-2">
            {selected ? (
              <RequestDetail req={selected} canManage={isRequestGM(user, selected.boutique_code)} refresh={refresh} />
            ) : (
              <div className="schedule-panel schedule-border rounded-3xl p-10 text-sm schedule-muted text-center">
                Pick a request on the left to see the details.
              </div>
            )}
          </div>
        </div>}
    </RequestVessel>
  );
}
